
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Smartphone, Download } from 'lucide-react';
import { isAndroidWebView } from '../lib/mobileBridge';

const AppDownloadBanner: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [promptShown, setPromptShown] = useState(false);

  useEffect(() => {
    if (isAndroidWebView()) return;

    const isAndroidDevice = /Android/i.test(navigator.userAgent);
    const isIOSDevice = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    setIsAndroid(isAndroidDevice);
    setIsIOS(isIOSDevice);

    const isDismissed = localStorage.getItem('app-banner-dismissed');
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;

    // If already installed or dismissed, don't show anything
    if (isDismissed || isStandalone) return;

    // Capture the install prompt event (Chrome/Edge on Android & Desktop)
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();

      // Stash the event so it can be triggered later.
      setDeferredPrompt(e);

      // Show the banner if not already shown
      if (!promptShown) {
        setPromptShown(true);
        setIsVisible(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    const handleAppInstalled = () => {
      console.log('PWA was installed');
      setIsVisible(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    // For iOS and desktop, show the banner after a short delay even if
    // beforeinstallprompt didn't fire (iOS doesn't fire that event)
    if (!promptShown && !isAndroidDevice && !isStandalone) {
      const timer = setTimeout(() => {
        setPromptShown(true);
        setIsVisible(true);
      }, 3000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.removeEventListener('appinstalled', handleAppInstalled);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [promptShown]);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('app-banner-dismissed', 'true');
  };

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Show the native install prompt (Chrome/Edge on Android & Desktop)
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response to the install prompt: ${outcome}`);
      setDeferredPrompt(null);
      setIsVisible(false);
    } else if (isIOS) {
      alert("To install Literally on iOS: Tap the Share button (box with arrow pointing up) in Safari, then select 'Add to Home Screen'.");
    } else {
      alert("To install Literally: Tap the browser menu (three dots) and select 'Install app' or 'Add to home screen'.");
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-4 left-4 right-4 z-[100] sm:bottom-6 sm:left-auto sm:right-6 sm:w-96 md:w-[420px]"
        >
          <div className="bg-slate-900/95 dark:bg-slate-900/95 text-white p-5 sm:p-6 rounded-3xl shadow-2xl border border-slate-700/60 backdrop-blur-2xl flex flex-col gap-4 relative overflow-hidden">
            {/* Top decorative gradient bar */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />

            {/* Header row with App Icon, Title, and Close Button */}
            <div className="flex items-start justify-between gap-3 pt-1">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-indigo-600/30 text-indigo-400 rounded-2xl flex items-center justify-center flex-shrink-0 border border-indigo-500/30 shadow-inner">
                  <Smartphone size={24} />
                </div>
                <div>
                  <h4 className="font-bold text-base leading-tight text-white">
                    Install Literally App
                  </h4>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Fast access & offline reading
                  </p>
                </div>
              </div>

              <button
                onClick={handleDismiss}
                title="Cancel and dismiss prompt"
                aria-label="Cancel or dismiss install prompt"
                className="p-1.5 hover:bg-white/10 rounded-full transition-colors text-slate-400 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body text */}
            <p className="text-xs text-slate-300 leading-relaxed">
              {isAndroid
                ? 'Add Literally to your home screen for a full-screen experience, push notifications, and offline reading.'
                : 'Add Literally to your home screen for quick 1-tap access and a native mobile feel.'}
            </p>

            {/* Action buttons row with Install, APK (if Android), and explicit Maybe Later / Cancel button */}
            <div className="flex flex-wrap items-center gap-2.5 pt-1">
              <button
                onClick={handleInstallClick}
                className="flex-1 min-w-[120px] bg-indigo-600 hover:bg-indigo-500 text-white py-2.5 px-4 rounded-xl text-xs font-bold shadow-lg shadow-indigo-600/30 active:scale-95 transition-all text-center flex items-center justify-center gap-1.5"
              >
                <Smartphone size={15} />
                Install App
              </button>

              {isAndroid && (
                <a
                  href="/literally-android.apk"
                  download="Literally-Android.apk"
                  className="bg-white/10 hover:bg-white/20 text-slate-200 py-2.5 px-3 rounded-xl text-xs font-bold border border-white/10 active:scale-95 transition-all flex items-center gap-1"
                >
                  <Download size={14} />
                  APK
                </a>
              )}

              <button
                onClick={handleDismiss}
                className="py-2.5 px-4 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-xl text-xs font-bold border border-slate-700 active:scale-95 transition-all text-center"
              >
                Maybe Later
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AppDownloadBanner;
